// Productivity Trend Chart - Issue #16
import React, { useState, useEffect } from 'react';
import {
    LineChart,
    Line,
    AreaChart,
    Area,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import {
    TrendingUp,
    TrendingDown,
    Calendar,
    BarChart2,
    LineChart as LineChartIcon,
    Activity
} from 'lucide-react';
import performanceService from '../../services/performance.service';

const ProductivityTrendChart = ({ employeeId = null }) => {
    const [trends, setTrends] = useState([]);
    const [period, setPeriod] = useState('6m');
    const [loading, setLoading] = useState(true);
    const [chartType, setChartType] = useState('line');
    const [metric, setMetric] = useState('productivity');
    const [message, setMessage] = useState('');

    useEffect(() => {
        fetchTrends();
    }, [period, employeeId]);

    const fetchTrends = async () => {
        try {
            setLoading(true);
            setMessage('');
            const response = await performanceService.getProductivityTrends(period, employeeId);
            setTrends(response.trends || []);
            if (response.message) {
                setMessage(response.message);
            }
        } catch (error) {
            console.error('Error fetching trends:', error);
            setMessage('Failed to load trend data');
        } finally {
            setLoading(false);
        }
    };

    const getMetricData = () => {
        return trends.map(item => ({
            period: item.period,
            value: parseFloat(item[`avg_${metric}`]) || 0,
            ...item
        }));
    };

    const getMetricLabel = () => {
        const labels = {
            productivity: 'Productivity (%)',
            rating: 'Overall Rating',
            technical: 'Technical Skills',
            communication: 'Communication',
            teamwork: 'Teamwork',
            problem_solving: 'Problem Solving',
            leadership: 'Leadership'
        };
        return labels[metric] || 'Productivity';
    };

    const calculateTrend = () => {
        if (trends.length < 2) return null;

        const data = getMetricData();
        const firstValue = data[0]?.value || 0;
        const lastValue = data[data.length - 1]?.value || 0;
        const change = lastValue - firstValue;
        const percentChange = firstValue > 0 ? ((change / firstValue) * 100).toFixed(1) : 0;

        return {
            change: change.toFixed(2),
            percentChange,
            isPositive: change >= 0
        };
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4">
                    <p className="font-semibold text-gray-900 dark:text-white mb-2">{label}</p>
                    <div className="space-y-1">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                            <span className="font-medium">{getMetricLabel()}:</span>{' '}
                            <span className="text-blue-600 dark:text-blue-400 font-semibold">
                                {payload[0].value}
                                {metric === 'productivity' ? '%' : ''}
                            </span>
                        </p>
                        {!employeeId && data.team_size && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Team Size: {data.team_size}
                            </p>
                        )}
                        {data.total_completed_goals > 0 && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Completed Goals: {data.total_completed_goals}
                            </p>
                        )}
                    </div>
                </div>
            );
        }
        return null;
    };

    const renderChart = () => {
        const data = getMetricData();

        if (data.length === 0) {
            return (
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-600 dark:text-gray-400">{message || 'No trend data available'}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                            Historical data will be collected over time
                        </p>
                    </div>
                </div>
            );
        }

        const commonProps = {
            data,
            margin: { top: 10, right: 30, left: 0, bottom: 0 }
        };

        const axisProps = {
            stroke: '#9CA3AF',
            style: { fontSize: '12px' }
        };

        switch (chartType) {
            case 'area':
                return (
                    <ResponsiveContainer width="100%" height={300}>
                        <AreaChart {...commonProps}>
                            <defs>
                                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                            <XAxis dataKey="period" {...axisProps} />
                            <YAxis {...axisProps} />
                            <Tooltip content={<CustomTooltip />} />
                            <Area
                                type="monotone"
                                dataKey="value"
                                stroke="#3B82F6"
                                fillOpacity={1}
                                fill="url(#colorValue)"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                );

            case 'bar':
                return (
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart {...commonProps}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                            <XAxis dataKey="period" {...axisProps} />
                            <YAxis {...axisProps} />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar dataKey="value" fill="#3B82F6" radius={[8, 8, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                );

            default:
                return (
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart {...commonProps}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                            <XAxis dataKey="period" {...axisProps} />
                            <YAxis {...axisProps} />
                            <Tooltip content={<CustomTooltip />} />
                            <Line
                                type="monotone"
                                dataKey="value"
                                stroke="#3B82F6"
                                strokeWidth={3}
                                dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                                activeDot={{ r: 6 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                );
        }
    };

    const trend = calculateTrend();

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Productivity Trends
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        {employeeId ? 'Individual performance over time' : 'Team performance over time'}
                    </p>
                </div>
                {trend && (
                    <div className={`flex items-center px-3 py-2 rounded-lg ${
                        trend.isPositive
                            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                    }`}>
                        {trend.isPositive ? (
                            <TrendingUp className="w-5 h-5 mr-2" />
                        ) : (
                            <TrendingDown className="w-5 h-5 mr-2" />
                        )}
                        <span className="text-sm font-semibold">
                            {trend.isPositive ? '+' : ''}{trend.percentChange}%
                        </span>
                    </div>
                )}
            </div>

            {/* Controls */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {/* Period Selector */}
                <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <Calendar className="w-4 h-4 mr-2" />
                        Time Period
                    </label>
                    <select
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                        <option value="3m">Last 3 Months</option>
                        <option value="6m">Last 6 Months</option>
                        <option value="12m">Last 12 Months</option>
                    </select>
                </div>

                {/* Metric Selector */}
                <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <Activity className="w-4 h-4 mr-2" />
                        Metric
                    </label>
                    <select
                        value={metric}
                        onChange={(e) => setMetric(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                        <option value="productivity">Productivity</option>
                        <option value="rating">Overall Rating</option>
                        <option value="technical">Technical Skills</option>
                        <option value="communication">Communication</option>
                        <option value="teamwork">Teamwork</option>
                        <option value="problem_solving">Problem Solving</option>
                        <option value="leadership">Leadership</option>
                    </select>
                </div>

                {/* Chart Type Selector */}
                <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <BarChart2 className="w-4 h-4 mr-2" />
                        Chart Type
                    </label>
                    <div className="flex space-x-2">
                        <button
                            onClick={() => setChartType('line')}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                chartType === 'line'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                            }`}
                        >
                            <LineChartIcon className="w-4 h-4 mx-auto" />
                        </button>
                        <button
                            onClick={() => setChartType('area')}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                chartType === 'area'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                            }`}
                        >
                            <Activity className="w-4 h-4 mx-auto" />
                        </button>
                        <button
                            onClick={() => setChartType('bar')}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                chartType === 'bar'
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                            }`}
                        >
                            <BarChart2 className="w-4 h-4 mx-auto" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Chart */}
            {loading ? (
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            ) : (
                renderChart()
            )}
        </div>
    );
};

export default ProductivityTrendChart;
